import { Component, computed, forwardRef, HostBinding, inject, input, model, OnInit, signal, viewChild } from '@angular/core';
import { InventoryActionCreateFormComponent } from '../inventory-action-create-form.component';
import {  FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Budget } from '../../../../../models/budget.model';
import { CheckIn } from '../../../../../models/checkin.model';
import { InventoryActionFormComponent } from '../../../../../shared/common-forms/inventory-action-form/inventory-action-form.component';
import { toInventoryActionProductForm } from '../../../../../models/inventory-action-product';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { HumanPipe } from '../../../../../shared/pipes/human.pipe';
import { FormGroupPipe } from '../../../../../shared/pipes/form-group.pipe';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatToolbarModule } from '@angular/material/toolbar';
import { AsyncPipe, NgClass } from '@angular/common';
import { DocumentSelectComponent } from '../../../../../shared/document-select/document-select.component';
import { MatCheckboxModule } from '@angular/material/checkbox';

type SourceDocumentType = CheckIn | Budget | undefined;

@Component({
  selector: 'app-pricing-create-form',
  templateUrl: './pricing-create-form.component.html',
  styleUrls: ['./pricing-create-form.component.scss', '../inventory-action-create-form.component.scss'],
  imports: [
    AsyncPipe,
    forwardRef(() => DocumentSelectComponent),
    HumanPipe,
    FormGroupPipe,
    FormsModule,
    forwardRef(() => InventoryActionFormComponent),
    MatAutocompleteModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatToolbarModule,
    NgClass,
    ReactiveFormsModule,
  ]
})
export class PricingCreateFormComponent extends InventoryActionCreateFormComponent implements OnInit {

  enableDocumentSelection = input<boolean>();

  isPluginMode = input<boolean>(false);

  sourceDocument = input<SourceDocumentType>(undefined);

  sourceDocumentType = input<string | undefined>();

  createFromDocumentMode = model(false);

  displayDocumentFn = (document: SourceDocumentType) => document?.correlative_id?.toString();

  form: FormGroup  = this.fb.group({
    document_id: [null],
    document_type: [null],
    inventory_action_products: this.fb.array([]),
    owner_id: [null, [Validators.required]],
    owner_type: ['Provider', [Validators.required]],
    warehouse_id: [null, [Validators.required]],
  });

  documents = signal<SourceDocumentType[]>([]);

  SOURCE_DOCUMENT_TYPES = ['Order', 'Budget'];

  inventoryActionFormComponent = viewChild(InventoryActionFormComponent);


  @HostBinding('class') background;

  
  readonly data = inject(MAT_DIALOG_DATA);

  _enableDocumentSelection = computed(() => this.enableDocumentSelection() || this.data?.enableDocumentSelection);
  _sourceDocument = computed(() => this.sourceDocument() || this.data?.sourceDocument);
  _sourceDocumentType = computed(() => this.sourceDocumentType() || this.data?.sourceDocumentType);


  get documentType(): string {
    return this.form.get('document_type').value;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.inventoryActionType = 'InventoryAction::Pricing';

    this.form.addControl('extra_params', this.fb.group({
      required_delivery_date: this.fb.control(null, Validators.required),
    }));

    this.form.get('owner_id').setValidators([Validators.required]);
    this.form.get('owner_type').setValue('Provider');
    this.providers = this.providerService.providers;

    if (this.isPluginMode()) {
      this.background = 'plugin-mode';
    }

    if (!this._enableDocumentSelection()) {
      this.form.get('document_id').setValue(this._sourceDocument().id);
      this.form.get('document_type').setValue(this._sourceDocumentType());
      this.onDocumentLoad(this._sourceDocument());
    }
  }

  protected getFormDataForSending() {
    const formData = super.getFormDataForSending();
    formData.draft = true;
    formData.owner_id = this.form.get('owner_id').value.at(0);
    formData.extra_params = {
      ...formData.extra_params,
      requester_id: this.userService.user$.value.id,
      required_products: formData.inventory_action_products_attributes,
      provider_name: this.providerService.find(formData.owner_id)?.name,
    };

    if (this.workInstanceId()) {
      formData.work_instance_id = this.workInstanceId();
    }

    return formData;
  }

  protected getFormDataForMultipleSending() {
    const formData = super.getFormDataForMultipleSending();

    formData.extra_params = {
      ...formData.extra_params,
      documents_count: this.selectedOwnerIds.length,
      provider_ids: this.selectedOwnerIds,
      provider_names: this.providerService.getFromIds(this.selectedOwnerIds).map((provider) => provider.name).join(', '),
      required_products: this.form.get('inventory_action_products').getRawValue(),
      warehouse_id: this.form.get('warehouse_id').value,
      warehouse_name: this.warehousesService.getWarehouseName(this.form.get('warehouse_id').value),
    };
      
    return formData;
  }

  protected getDocumentsAttributes() {
    return this.selectedOwnerIds.map((ownerId) => (
      {
        ...this.getFormDataForSending(),
        extra_params: {
          ...this.getFormDataForSending().extra_params,
          provider_name: this.providerService.find(ownerId)?.name,
        },
        owner_id: ownerId,
      })
    );
  }

  private get selectedOwnerIds() {
    return this.form.get('owner_id').value?.filter((id: number | null) => id)
  }

  protected isMultiple() {
    return this.selectedOwnerIds.length > 1;
  }

  toggleCreateFromDocumentMode(enabled: boolean) {
    if (!enabled) {
      this.form.get('document_id').setValue(null, { emitEvent: false });
      this.form.get('document_type').setValue(null, { emitEvent: false });
    }
  }

  onDocumentLoad(document) {
    if (!document) {
      return;
    }

    let products = [];
    if (this.documentType === 'Order') {
      document = document as CheckIn;
      products = [
        ...document.inventory_action_for_consume.inventory_action_products,
        ...document.budgets.flatMap(
          (budget) => budget.inventory_actions.flatMap(
            (inventoryAction) => inventoryAction.inventory_action_products
          )
        ),
      ];

      this.form.get('warehouse_id').setValue(document.warehouse_id);
    } else if (this.documentType === 'Budget') {
      document = document as Budget;
      products = document.inventory_actions.flatMap(
        (inventoryAction) => inventoryAction.inventory_action_products
      );
      this.form.get('warehouse_id').setValue(document.warehouse_id);
    }
  
    this.form.controls.inventory_action_products = this.fb.array(
      products.map(
        (product) => toInventoryActionProductForm({ ...product, id: undefined }),
      )
    );
    this.inventoryActionFormComponent()?.initializeForm();
  }
}
